import {
  CmBox,
  CmCard,
  CmCheckBox,
  CmDatePicker,
  CmErrorText,
  CmFlex,
  CmFormControl,
  CmFormErrorMessage,
  CmFormLabel,
  CmHelpterText,
  CmInput,
  CmRadio,
  CmRadioGroup,
  CmScrollBox,
  CmStack,
  CmText,
} from '@/components/uiParts';
import { OrderMethod, orderMethods } from '@/domain/models/Order/orderMethod';
import { itemForm } from '@/pages/camel-order/coupons/create/service';
import { Page } from '@/pages/camel-order/coupons/create/useCamelOrderCouponItemForm';
import { ItemFormError } from '@/pages/camel-order/coupons/create/validate';

type Props = {
  page: Page;
  name: string;
  couponCode: string;
  discount: string | undefined;
  conditionType: string;
  conditionValue: string | undefined;
  maxUsagePerUser: string | undefined;
  validFrom: Date | null;
  validTo: Date | null;
  status: string;
  selectedAvailableOrderMethod: OrderMethod[];
  error: ItemFormError;
  handleInputName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputDiscount: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputConditionValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMaxUsagePerUser: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeAvailableOrderMethods: (
    OrderMethod: OrderMethod,
    flag: boolean
  ) => void;
  handleInputValidFrom: (date: Date | null) => void;
  handleInputValidTo: (date: Date | null) => void;
  handleInputStatus: (value: React.ReactText) => void;
  height: string;
};

const isOrderMethodChecked = (
  orderMethod: OrderMethod,
  selectedAvailableOrderMethod: string[]
): boolean => {
  return selectedAvailableOrderMethod.includes(orderMethod);
};

export const CamelOrderCouponItemsForm: React.FC<Props> = ({
  page,
  name,
  couponCode,
  discount,
  conditionValue,
  maxUsagePerUser,
  validFrom,
  validTo,
  selectedAvailableOrderMethod,
  status,
  error,
  handleInputName,
  handleInputCode,
  handleInputDiscount,
  handleInputConditionValue,
  handleInputMaxUsagePerUser,
  handleChangeAvailableOrderMethods,
  handleInputValidFrom,
  handleInputValidTo,
  handleInputStatus,
  height,
}): JSX.Element => {
  return (
    <CmCard height={height} padding={'20px 40px 10px 40px'}>
      <CmScrollBox height="100%">
        <CmStack>
          <CmBox>
            <CmFormLabel isRequired fontWeight={'bold'} pt="10px">
              {itemForm.name.label}
            </CmFormLabel>
            <CmHelpterText>{itemForm.name.helperText}</CmHelpterText>
            <CmInput
              isRequired
              isDisabled={page === 'edit'}
              id={itemForm.name.key}
              isInvalid={error.name.isError}
              value={name}
              onChange={handleInputName}
            />
            <CmErrorText>{error.name.message}</CmErrorText>

            <CmFormLabel isRequired fontWeight={'bold'} pt="10px">
              {itemForm.couponCode.label}
            </CmFormLabel>
            <CmHelpterText>{itemForm.couponCode.helperText}</CmHelpterText>
            <CmInput
              isRequired
              isDisabled={page === 'edit'}
              id={itemForm.couponCode.key}
              isInvalid={error.couponCode.isError}
              value={couponCode}
              onChange={handleInputCode}
            />
            <CmErrorText>{error.couponCode.message}</CmErrorText>

            <CmFormLabel isRequired fontWeight={'bold'} pt="10px">
              {itemForm.discount.label}
            </CmFormLabel>

            <CmHelpterText>{itemForm.discount.helperText}</CmHelpterText>
            <CmStack direction="row" align="center">
              <CmInput
                isRequired
                isDisabled={page === 'edit'}
                id={itemForm.discount.key}
                isInvalid={error.discount.isError}
                value={discount}
                onChange={handleInputDiscount}
              />
              <CmText fontWeight={'bold'}>円</CmText>
            </CmStack>
            <CmErrorText>{error.discount.message}</CmErrorText>

            <CmFormLabel fontWeight="bold" pt="10px">
              {itemForm.maxUsagePerUser.label}
            </CmFormLabel>
            <CmInput
              isDisabled={page === 'edit'}
              id={itemForm.maxUsagePerUser.key}
              isInvalid={error.maxUsagePerUser.isError}
              value={maxUsagePerUser}
              onChange={handleInputMaxUsagePerUser}
            />
            <CmErrorText>{error.maxUsagePerUser.message}</CmErrorText>

            <CmFormLabel fontWeight={'bold'} pt="10px" isRequired>
              {itemForm.availableOrderMethod.label}
            </CmFormLabel>
            <CmStack direction="row" align="center">
              {orderMethods.map((option, index) => (
                <CmCheckBox
                  key={index}
                  ml={index > 0 ? '20px' : undefined}
                  isChecked={isOrderMethodChecked(
                    option.value,
                    selectedAvailableOrderMethod
                  )}
                  value={option.value}
                  onChange={(e) => {
                    handleChangeAvailableOrderMethods(
                      option.value,
                      e.target.checked
                    );
                  }}
                >
                  {option.name}
                </CmCheckBox>
              ))}
            </CmStack>
            <CmErrorText>{error.availableOrderMethod.message}</CmErrorText>

            <CmFormLabel fontWeight={'bold'} pt="10px">
              {itemForm.conditionValue.label}
            </CmFormLabel>
            <CmStack direction="row" align="center">
              <CmInput
                isDisabled={page === 'edit'}
                id={itemForm.conditionValue.key}
                isInvalid={error.conditionValue.isError}
                value={conditionValue}
                onChange={handleInputConditionValue}
              />
              <CmText fontWeight={'bold'}>円</CmText>
            </CmStack>
            <CmErrorText>{error.conditionValue.message}</CmErrorText>
            <CmFormControl
              isInvalid={error.validFrom.isError}
              isDisabled={page === 'edit'}
              pt="10px"
            >
              <CmFormLabel fontWeight="bold">有効期間</CmFormLabel>
              <CmFlex
                direction="row"
                justifyContent="flex-start"
                align="center"
              >
                <CmFlex
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <CmDatePicker
                    selected={validFrom}
                    onChange={handleInputValidFrom}
                    dateFormat="yyyy-MM-dd HH:mm"
                    showTimeSelect
                  />
                </CmFlex>
                <CmText mx={2}>〜</CmText>
                <CmFlex
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <CmDatePicker
                    selected={validTo}
                    onChange={handleInputValidTo}
                    dateFormat="yyyy-MM-dd HH:mm"
                    showTimeSelect
                  />
                </CmFlex>
              </CmFlex>
              <CmFormErrorMessage>
                {error.validFrom.isError && error.validFrom.message}
                {error.validTo.isError && error.validTo.message}
              </CmFormErrorMessage>
            </CmFormControl>
            <CmFormLabel isRequired fontWeight={'bold'} pt="10px">
              {itemForm.status.label}
            </CmFormLabel>
            <CmRadioGroup
              defaultValue={status}
              value={status}
              onChange={handleInputStatus}
            >
              <CmStack>
                <CmRadio size="md" value="ACTIVE">
                  有効
                </CmRadio>
                <CmRadio size="md" value="INACTIVE">
                  無効
                </CmRadio>
              </CmStack>
            </CmRadioGroup>
          </CmBox>
        </CmStack>
      </CmScrollBox>
    </CmCard>
  );
};
